import * as React from 'react';
import { cn } from '../utils';
import { DEFAULT_PLACEHOLDER } from '@insio/format-utils';

export interface LabelValueFieldProps {
  /** Label text - displayed in uppercase by default, small, muted */
  label: string;
  /** Value to display - can be string, number, or React node */
  value: React.ReactNode | null | undefined;
  /** Additional class name for the container */
  className?: string;
  /** Additional class name for the label */
  labelClassName?: string;
  /** Additional class name for the value */
  valueClassName?: string;
  /** Whether the value should be bold/semibold */
  bold?: boolean;
  /** Whether the label should be uppercase (default: true) */
  uppercase?: boolean;
  /** Test ID for testing */
  testId?: string;
}

/**
 * LabelValueField - A label-value pair component for structured information display
 *
 * Displays a small uppercase label above a value. Commonly used in forms,
 * invoices, receipts, and detail views where information is presented
 * in a structured key-value format.
 *
 * @example
 * ```tsx
 * <LabelValueField label="Account Number" value="123456789/0100" bold />
 * <LabelValueField label="SWIFT" value={bankDetails?.swift ?? '—'} />
 * <LabelValueField label="Issue Date" value="26. 2. 2026" uppercase={false} />
 * ```
 */
export function LabelValueField({
  label,
  value,
  className,
  labelClassName,
  valueClassName,
  bold = false,
  uppercase = true,
  testId
}: LabelValueFieldProps) {
  return (
    <div className={cn('flex-1', className)} data-testid={testId}>
      <p
        className={cn(
          'text-xs text-foreground-muted mb-xs',
          uppercase && 'uppercase',
          labelClassName
        )}
      >
        {label}
      </p>
      <div className={cn('text-sm text-foreground-base', bold && 'font-semibold', valueClassName)}>
        {value ?? DEFAULT_PLACEHOLDER}
      </div>
    </div>
  );
}

LabelValueField.displayName = 'LabelValueField';
